/* eslint-disable @typescript-eslint/default-param-last */
/* eslint-disable @typescript-eslint/default-param-last */
import {FileUploadService} from '../../analysis/api/file-upload.service';

import {HttpClient, HttpEventType, HttpParams} from '@angular/common/http';
import {EventEmitter, Inject, Injectable, Output} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {WINDOW} from '@oblique/oblique';

import {GesamtverdienstUndLohnbestandteile} from '../dtos/cockpit/gesamtverdienst-und-lohnbestandteile';
import {DataResult, DataResultM2} from '../dtos/data-result';
import {AdditionalInfo} from '../dtos/wizard/additional-info';
import {EnterpriseInfo} from '../dtos/wizard/enterprise-info';
import {TEMOST_CONST} from '../EnumsConstants/global-enum';
import {
  ProgressStatus,
  ProgressStatusEnum
} from '../EnumsConstants/ProgressStatus';
import {DataStorageService} from './data-storage.service';
import {Cockpit, CockpitM2} from '../dtos/cockpit/cockpit';
import {ComplianceRiskFunktionsebene} from '../dtos/result/result-m2/compliance-risk-funktionsebene';
import {EnterpriseInfoCompletionDialogService} from '../../result/result-shared/enterprise-info-completion-dialog/enterprise-info-completion-dialog.service';
import {FilenameService} from './filename.service';
import {SvgDownloadService} from './svg-download.service';
import {AnzahlMitarbeitendeNachGrungesamtheit} from 'shared/dtos/cockpit/personalbestand';

@Injectable({
  providedIn: 'root'
})
export class DownloadService {
  @Output() public readonly downloadStatus: EventEmitter<ProgressStatus>;

  disabled: boolean;

  constructor(
    private readonly fileUploadService: FileUploadService,
    private readonly http: HttpClient,
    private readonly translateService: TranslateService,
    private readonly storage: DataStorageService,
    private readonly completionDialog: EnterpriseInfoCompletionDialogService,
    private readonly filenameService: FilenameService,
    private readonly svgDownloadService: SvgDownloadService,
    @Inject(WINDOW) private readonly window
  ) {
    this.downloadStatus = new EventEmitter<ProgressStatus>();
  }

  async exportExcelAnalyseData<T>(
    dataInput: T,
    selectedLanguage: string,
    sender: string,
    module: string
  ) {
    const isActiveKontrollModus = await this.storage.getKontrollmodus();
    this.downloadStatus.emit({status: ProgressStatusEnum.START});
    (
      await this.fileUploadService.exportExcelAnalyseData(
        dataInput,
        selectedLanguage,
        sender
      )
    ).subscribe(
      data => {
        const filename = this.getDownloadFileName(
          sender,
          isActiveKontrollModus
        ).replace('{{module}}', module);
        switch (data.type) {
          case HttpEventType.DownloadProgress:
            this.downloadStatus.emit({
              status: ProgressStatusEnum.IN_PROGRESS,
              percentage: Math.round((data.loaded / data.total) * 100)
            });
            break;
          case HttpEventType.Response:
            this.downloadStatus.emit({status: ProgressStatusEnum.COMPLETE});
            this.downloadFile(data.body, filename);
            break;
          default:
            break;
        }
      },
      error => {
        this.downloadStatus.emit({status: ProgressStatusEnum.ERROR});
        console.error('ERROR when exporting analysis data', error);
      }
    );
  }

  async downloadReport(
    ai: AdditionalInfo,
    ei: EnterpriseInfo,
    res: DataResult,
    gvUlbst: GesamtverdienstUndLohnbestandteile,
    svg: SVGSVGElement | HTMLElement,
    scn: number,
    module: string
  ) {
    const langCode = this.translateService.currentLang;
    const url = 'api/document/report';
    const filename = this.filenameService
      .getFilename('m1.filenames.report')
      .replace('{{module}}', module);
    const tachoBlob = await this.svgDownloadService.convertTo(svg, 'image/png');

    this.http
      .post(
        url,
        {
          enterpriseInfo: ei,
          additionalInfo: ai,
          dataResult: res,
          gesamtverdienstUndLohnbestandteile: gvUlbst,
          tachoBlob,
          scenario: scn
        },
        {
          params: new HttpParams().set('culture', langCode),
          responseType: 'blob'
        }
      )
      .subscribe({
        next: data => {
          this.downloadFile(data, filename);
        },
        error: error => {
          console.error(error);
        }
      });
  }

  async downloadReportM2(
    ai: AdditionalInfo,
    ei: EnterpriseInfo,
    res: DataResultM2,
    gvUlbst: GesamtverdienstUndLohnbestandteile,
    pers: AnzahlMitarbeitendeNachGrungesamtheit,
    svg: SVGSVGElement | HTMLElement,
    scn: number,
    module: string
  ) {
    const langCode = this.translateService.currentLang;
    const url = 'api/document/report/m2';
    const filename = this.filenameService
      .getFilename('m1.filenames.report')
      .replace('{{module}}', module);
    const tachoBlob = await this.svgDownloadService.convertTo(svg, 'image/png');

    this.http
      .post(
        url,
        {
          enterpriseInfo: ei,
          additionalInfo: ai,
          dataResult: res,
          gesamtverdienstUndLohnbestandteile: gvUlbst,
          AnzahlMitarbeitendeNachGrundgesamtheit: pers,
          tachoBlob,
          scenario: scn
        },
        {
          params: new HttpParams().set('culture', langCode),
          responseType: 'blob'
        }
      )
      .subscribe({
        next: data => {
          this.downloadFile(data, filename);
        },
        error: error => {
          console.error(error);
        }
      });
  }

  async downloadSummary(
    ai: AdditionalInfo,
    ei: EnterpriseInfo,
    res: DataResult,
    svg: SVGSVGElement | HTMLElement,
    scn: number,
    module: string
  ) {
    const langCode = this.translateService.currentLang;
    const url = 'api/document/summary/m1';
    const filename = this.filenameService
      .getFilename('common.filenames.summary')
      .replace('{{module}}', module);
    const tachoBlob = await this.svgDownloadService.convertTo(svg, 'image/png');

    this.http
      .post(
        url,
        {
          enterpriseInfo: ei,
          additionalInfo: ai,
          dataResult: res,
          tachoBlob,
          scenario: scn
        },
        {
          params: new HttpParams().set('culture', langCode),
          responseType: 'blob'
        }
      )
      .subscribe({
        next: data => {
          this.downloadFile(data, filename);
        },
        error: error => {
          console.error(error);
        }
      });
  }

  async downloadSummaryM2(
    module: string,
    scn: number,
    svg: SVGSVGElement | HTMLElement,
    res?: DataResultM2,
    ai?: AdditionalInfo,
    ei?: EnterpriseInfo
  ) {
    if (res === undefined) {
      res = (await this.storage.getResultData()) as DataResultM2;
    }
    if (ai === undefined) {
      ai = await this.storage.getAdditionalInfo();
    }
    if (ei === undefined) {
      ei = await this.storage.getEnterpriseInfo();
    }

    const langCode = this.translateService.currentLang;
    const url = 'api/document/summary/m2';
    const filename = this.filenameService
      .getFilename('common.filenames.summary')
      .replace('{{module}}', module);
    const tachoBlob = await this.svgDownloadService.convertTo(svg, 'image/png');

    this.http
      .post(
        url,
        {
          enterpriseInfo: ei,
          additionalInfo: ai,
          dataResult: res,
          tachoBlob,
          scenario: scn
        },
        {
          params: new HttpParams().set('culture', langCode),
          responseType: 'blob'
        }
      )
      .subscribe({
        next: data => {
          this.downloadFile(data, filename);
        },
        error: error => {
          console.error(error);
        }
      });
  }

  async downloadSelbstdeklaration(
    additionalInfo?: AdditionalInfo,
    enterpriseInfo?: EnterpriseInfo,
    dataResult?: DataResult | DataResultM2,
    cockpit?: Cockpit | CockpitM2,
    complianceRiskFunktionsebene?: ComplianceRiskFunktionsebene,
    scenario?: number
  ) {
    const isForM2 = await this.storage.getIsForModule2();
    if (!additionalInfo) {
      additionalInfo = await this.storage.getAdditionalInfo();
    }
    if (!enterpriseInfo) {
      enterpriseInfo = await this.storage.getEnterpriseInfo();
    }
    if (!dataResult) {
      dataResult = await this.storage.getResultData();
    }
    if (!cockpit) {
      cockpit = await this.storage.getCockpitData();
    }

    if (!complianceRiskFunktionsebene && isForM2) {
      complianceRiskFunktionsebene =
        await this.storage.getComplianceRiskFunktionsebene();
    }

    await this.handleDownload(
      'selbstdeklaration',
      'common.filenames.selfdeclaration',
      isForM2,
      {
        enterpriseInfo,
        additionalInfo,
        dataResult,
        cockpit,
        complianceRiskFunktionsebene,
        scenario
      }
    );
  }

  async downloadPruefbericht(scenario?: number, robustscore?: number) {
    await this.completionDialog.performCompletion();
    const isForM2 = await this.storage.getIsForModule2();
    let data = null;
    if (isForM2) {
      data = await this.storage.getExtendedDocumentDataM2(
        scenario,
        robustscore
      );
    } else {
      data = await this.storage.getExtendedDocumentDataM1(scenario);
    }
    await this.handleDownload(
      'pruefbericht',
      'common.filenames.report-verification',
      isForM2,
      data
    );
  }

  getDownloadFileName(sender: string, isActiveKontrollModus: boolean) {
    switch (sender) {
      case TEMOST_CONST.Sender.Module1:
      case TEMOST_CONST.Sender.Module2:
        return isActiveKontrollModus
          ? this.filenameService.getFilename(
              'common.filenames.exportfile-expert'
            )
          : this.filenameService.getFilename('common.filenames.exportfile');
      case TEMOST_CONST.Sender.ComplianceRiskVergleichspaare:
        return this.filenameService.getFilename(
          'm2.filenames.export-detailed-result'
        );
      case TEMOST_CONST.Sender.ComplianceRiskVergleichspaareForKontrollmodus:
        return this.filenameService.getFilename(
          'm2.filenames.export-detailed-result-expert'
        );
      default:
        console.error('ERROR', sender);
        throw new Error('Unexpected getDownloadFileName');
    }
  }

  private async handleDownload(
    document: 'selbstdeklaration' | 'pruefbericht',
    filenameKey: string,
    isForM2: boolean,
    body: any
  ) {
    const langCode = this.translateService.currentLang;
    const moduletext = isForM2 ? 'm2' : 'm1';
    const url = `api/document/${document}/${moduletext}`;
    const filename = this.filenameService
      .getFilename(filenameKey)
      .replace('{{module}}', moduletext.toUpperCase());

    this.http
      .post(url, body, {
        params: new HttpParams().set('culture', langCode),
        responseType: 'blob'
      })
      .subscribe({
        next: data => {
          this.downloadFile(data, filename);
        },
        error: error => {
          console.error(error);
        }
      });
  }

  private downloadFile(data: Blob, filename: string) {
    const downloadedFile = new Blob([data], {type: data.type});
    if (this.window.navigator?.msSaveOrOpenBlob) {
      this.window.navigator.msSaveOrOpenBlob(downloadedFile, filename);
    } else {
      const a = document.createElement('a');
      a.setAttribute('style', 'display:none;');
      a.setAttribute('download', filename);
      document.body.appendChild(a);
      a.download = filename;
      a.href = URL.createObjectURL(downloadedFile);
      a.target = '_blank';
      a.click();
      document.body.removeChild(a);
    }
  }
}
